import ModalComponent from '../../../../components/ModalComponent';
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Box, Grid2, IconButton } from '@mui/material';
import { IDefenseBridgeProps } from '../../../../services/Defense/Bridge/types';
import { DefenseBridgeConfigSchema } from '../../../../services/Defense/Bridge/schemas';
import zod from 'zod';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import ButtonComponent from '../../../../components/ButtonComponent';
import TextFieldComponent from '../../../../components/TextFieldComponent';
import TooltipComponent from '../../../../components/TooltipComponent';
import SwitchComponent from '../../../../components/SwitchComponent';
import { Delete } from '@mui/icons-material';

interface IDefenseBridgeConfigurationModalProps {
  isOpen: boolean;
  onClose?: () => void;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  handleCreateOrUpdate: (data: any, reset: () => void) => void;
  handleDelete: (id: number, reset: () => void) => void;
  bridge?: IDefenseBridgeProps;
}

type DefenseBridgeInput = zod.infer<typeof DefenseBridgeConfigSchema>;

const DefenseBridgeConfigurationModalComponent = ({
  isOpen,
  setIsOpen,
  handleCreateOrUpdate,
  handleDelete,
  bridge,
}: IDefenseBridgeConfigurationModalProps) => {
  const { control, handleSubmit, reset } = useForm<DefenseBridgeInput>({
    resolver: zodResolver(DefenseBridgeConfigSchema),
    defaultValues: {
      name: '',
      description: '',
      isActive: true,
      serverIp: '',
      serverPort: 80,
      accessKey: '',
      secretKey: '',
    },
  });

  const [selectedBridge, setSelectedBridge] = useState<IDefenseBridgeProps>();

  const handleClose = () => {
    reset();
    setIsOpen(false);
    setSelectedBridge(undefined);
  };

  const onCreateOrUpdate = (data: DefenseBridgeInput) => {
    handleCreateOrUpdate(data, reset);
  };

  const onDelete = () => {
    if (selectedBridge) {
      handleDelete(selectedBridge.id, reset);
    }
  };

  const getDeleteButton = () => {
    if (selectedBridge && selectedBridge.id) {
      return (
        <Box display={'flex'} justifyContent={'end'}>
          <IconButton
            size={'small'}
            color={'error'}
            onClick={handleSubmit(onDelete)}
          >
            <Delete />
          </IconButton>
        </Box>
      );
    } else {
      return <span />;
    }
  };

  useEffect(() => {
    if (bridge) {
      reset(bridge, { keepDefaultValues: true });
      setSelectedBridge(bridge);
    } else reset();
  }, [bridge]);

  return (
    <ModalComponent
      onClose={handleClose}
      isOpen={isOpen}
      title="Configurar Bridge"
      additionalTopRightComponent={getDeleteButton()}
    >
      <Grid2
        container
        direction={'column'}
        justifyContent={'center'}
        alignItems={'center'}
        size={12}
        spacing={3}
      >
        <Grid2
          display={'flex'}
          flexDirection={{ xs: 'column', md: 'row' }}
          justifyContent={'center'}
          alignItems={{ xs: 'center', md: 'normal' }}
          size={12}
          gap={{ xs: 0, md: 3 }}
        >
          <Grid2
            size={{ xs: 12, lg: 6 }}
            display={'column'}
            alignItems={'center'}
            justifyContent={'center'}
          >
            <Controller
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <TextFieldComponent
                  label="Nome"
                  onChangeText={(val) => onChange(val)}
                  type={'text'}
                  errorMessage={error?.message}
                  value={value}
                />
              )}
              name={'name'}
              control={control}
            />
            <Controller
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <TextFieldComponent
                  label="IP do Servidor"
                  onChangeText={(val) => onChange(val)}
                  type={'text'}
                  errorMessage={error?.message}
                  value={value}
                  mask={'000.000.000.000'}
                  maxLength={15}
                />
              )}
              name={'serverIp'}
              control={control}
            />
            <Controller
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <TextFieldComponent
                  label="Porta do Servidor"
                  onChangeText={(val) => onChange(Number(val))}
                  type={'text'}
                  errorMessage={error?.message}
                  value={value}
                />
              )}
              name={'serverPort'}
              control={control}
            />
            <Controller
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <TextFieldComponent
                  label="Descrição"
                  onChangeText={(val) => onChange(val)}
                  type={'text'}
                  errorMessage={error?.message}
                  value={value}
                  multiline
                  maxRows={4}
                />
              )}
              name={'description'}
              control={control}
            />
          </Grid2>
          <Grid2
            size={{ xs: 12, lg: 6 }}
            display={'column'}
            alignItems={'center'}
            justifyContent={'center'}
          >
            <Controller
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <TextFieldComponent
                  label="Chave de Acesso"
                  onChangeText={(val) => onChange(val)}
                  type={'text'}
                  errorMessage={error?.message}
                  value={value}
                />
              )}
              name={'accessKey'}
              control={control}
            />
            <Controller
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <TextFieldComponent
                  label="Chave Secreta"
                  onChangeText={(val) => onChange(val)}
                  type={'text'}
                  errorMessage={error?.message}
                  value={value}
                />
              )}
              name={'secretKey'}
              control={control}
            />
            <Controller
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <Box width={'14%'}>
                  <TooltipComponent
                    title={value ? 'Ativo' : 'Inativo'}
                    placement={'right'}
                  >
                    <SwitchComponent
                      label="Status"
                      value={value}
                      onChange={(value) => onChange(value)}
                      color={'primary'}
                    />
                  </TooltipComponent>
                </Box>
              )}
              name={'isActive'}
              control={control}
            />
          </Grid2>
        </Grid2>
        <ButtonComponent
          type="submit"
          variant="contained"
          color="primary"
          fullWidth
          onClick={handleSubmit(onCreateOrUpdate)}
        >
          Salvar
        </ButtonComponent>
      </Grid2>
    </ModalComponent>
  );
};

export default DefenseBridgeConfigurationModalComponent;
